import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, ShoppingBag, Truck, Shield, Store as StoreIcon, Globe, CreditCard, Landmark, Zap } from "lucide-react";

const ENVIO_LOCAL_DEFAULT = 49;

const ENVIOS = [
  { id: "90min", Icon: Zap, titulo: "Entrega en 90 minutos", desc: "Repartidor de la plataforma en tu zona." },
  { id: "uber", Icon: Truck, titulo: "Recibir mi pedido por Uber", desc: "El comercio envía con Uber. El costo del viaje se coordina con la tienda." },
  { id: "foraneo", Icon: Globe, titulo: "Envío foráneo (otra población)", desc: "En envíos foráneos el tiempo de entrega es hasta en 72 horas." },
];

const PAGOS = [
  { id: "stripe", Icon: CreditCard, titulo: "Tarjeta (Stripe)", desc: "Débito o crédito, pago inmediato." },
  { id: "transferencia", Icon: Landmark, titulo: "Transferencia BBVA", desc: "Te mostramos la CLABE al confirmar." },
];

const FORANEO_INIT = { nombre: "", telefono: "", calle: "", colonia: "", ciudad: "", estado: "", codigo_postal: "", referencias: "" };

export default function Cart() {
  const { items, plazaId, remove, updateQty, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [direccion, setDireccion] = useState("");
  const [loading, setLoading] = useState(false);
  const [envioPlaza, setEnvioPlaza] = useState(ENVIO_LOCAL_DEFAULT);
  const [tiendasMap, setTiendasMap] = useState({});
  const [metodoEnvio, setMetodoEnvio] = useState("90min");
  const [metodoPago, setMetodoPago] = useState("stripe");
  const [foraneo, setForaneo] = useState(FORANEO_INIT);

  useEffect(() => {
    if (plazaId) api.get(`/plazas/${plazaId}`).then(r => setEnvioPlaza(r.data.costo_envio)).catch(() => {});
    const ids = [...new Set(items.map(i => i.tienda_id))];
    Promise.all(ids.map(id => api.get(`/tiendas/${id}`).then(r => r.data).catch(() => null))).then(list => {
      const map = {};
      list.filter(Boolean).forEach(t => { map[t.id] = t; });
      setTiendasMap(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plazaId, items.length]);

  useEffect(() => {
    if (user) setForaneo(f => ({ ...f, nombre: f.nombre || user.nombre || "", telefono: f.telefono || user.telefono || "" }));
  }, [user]);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach(i => {
      if (!g[i.tienda_id]) g[i.tienda_id] = { items: [], subtotal: 0 };
      g[i.tienda_id].items.push(i);
      g[i.tienda_id].subtotal += i.precio * i.cantidad;
    });
    return g;
  }, [items]);

  const envio = metodoEnvio === "90min" && items.length ? envioPlaza : 0;
  const total = subtotal + envio;
  const setF = (k, v) => setForaneo(f => ({ ...f, [k]: v }));

  const checkout = async () => {
    if (!user) { nav("/login?next=/carrito"); return; }
    if (user.role !== "cliente") { toast.error("Solo los clientes pueden comprar"); return; }
    if (!items.length) return;
    if (metodoEnvio === "foraneo") {
      const faltan = ["nombre", "telefono", "calle", "ciudad", "estado", "codigo_postal"].filter(k => !foraneo[k]);
      if (faltan.length) { toast.error("Completa los datos de envío foráneo"); return; }
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        plaza_id: plazaId || null,
        items: items.map(i => ({ producto_id: i.producto_id, cantidad: i.cantidad })),
        origin_url: window.location.origin,
        tipo_entrega: "Inmediata",
        direccion_entrega: direccion || user.direccion_predeterminada || "",
        metodo_envio: metodoEnvio,
        metodo_pago: metodoPago,
        datos_foraneo: metodoEnvio === "foraneo" ? foraneo : null,
      });
      clear();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else nav(`/pago/${data.orden_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="overline text-zinc-500">Checkout</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Tu carrito</h1>

        {items.length === 0 ? (
          <div className="mt-10 border border-dashed border-zinc-300 rounded-sm p-16 text-center">
            <ShoppingBag className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
            <div className="text-zinc-500">Tu carrito está vacío</div>
            <Link to="/"><Button className="mt-4 rounded-sm bg-[#FF3366] hover:bg-[#E62958]">Ir al catálogo</Button></Link>
          </div>
        ) : (
          <div className="mt-8 grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-5">
              {Object.entries(grouped).map(([tiendaId, group]) => {
                const tienda = tiendasMap[tiendaId];
                return (
                  <div key={tiendaId} className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2 bg-zinc-50 flex-wrap">
                      <StoreIcon className="w-4 h-4 text-[#FF3366]" />
                      <Link to={`/t/${tiendaId}`} className="font-display font-semibold tracking-tight hover:text-[#FF3366]">{tienda?.nombre_local || "Tienda"}</Link>
                      {tienda?.numero_local && tienda.numero_local !== "N/A" && <span className="text-xs text-zinc-500">· Local {tienda.numero_local}</span>}
                      {tienda?.envia_uber && <span className="text-[10px] bg-zinc-900 text-white px-1.5 py-0.5 rounded-sm">Envía por Uber</span>}
                      {tienda?.envia_foraneo && <span className="text-[10px] bg-[#D4FF00] px-1.5 py-0.5 rounded-sm">Envío foráneo 72 h</span>}
                      <span className="ml-auto font-medium text-sm">${group.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="divide-y divide-zinc-100">
                      {group.items.map(i => (
                        <div key={i.producto_id} data-testid={`cart-item-${i.producto_id}`} className="flex gap-4 p-4">
                          <div className="w-20 h-20 rounded-sm bg-zinc-100 overflow-hidden shrink-0">
                            {i.imagen_url && <img src={i.imagen_url} alt={i.nombre} className="w-full h-full object-cover" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold tracking-tight truncate">{i.nombre}</div>
                            <div className="text-sm text-zinc-500 mt-0.5">${i.precio.toFixed(2)}</div>
                            <div className="mt-2 flex items-center gap-3">
                              <div className="flex items-center border border-zinc-300 rounded-sm text-sm">
                                <button onClick={() => updateQty(i.producto_id, i.cantidad - 1)} className="w-8 h-8 hover:bg-zinc-100">−</button>
                                <span className="w-8 text-center">{i.cantidad}</span>
                                <button onClick={() => updateQty(i.producto_id, i.cantidad + 1)} className="w-8 h-8 hover:bg-zinc-100">+</button>
                              </div>
                              <button data-testid={`cart-remove-${i.producto_id}`} onClick={() => remove(i.producto_id)} className="text-zinc-400 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="font-display font-bold text-lg">${(i.precio * i.cantidad).toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Método de envío */}
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="overline text-zinc-500 mb-3">¿Cómo quieres recibir tu pedido?</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {ENVIOS.map(op => (
                    <button key={op.id} type="button" data-testid={`envio-${op.id}`} onClick={() => setMetodoEnvio(op.id)}
                      className={`text-left p-3 rounded-sm border-2 transition-colors ${metodoEnvio === op.id ? "border-[#FF3366] bg-[#FF3366]/5" : "border-zinc-200 hover:border-zinc-400"}`}>
                      <op.Icon className="w-4 h-4 mb-2 text-[#FF3366]" />
                      <div className="font-display font-bold text-sm leading-tight">{op.titulo}</div>
                      <div className="text-[11px] text-zinc-500 mt-1">{op.desc}</div>
                    </button>
                  ))}
                </div>

                {metodoEnvio !== "foraneo" && (
                  <div className="mt-4">
                    <label className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Dirección de entrega</label>
                    <Input data-testid="cart-address" value={direccion} onChange={e => setDireccion(e.target.value)}
                      placeholder={user?.direccion_predeterminada || "Ej: Calle 123, Colonia..."} className="mt-1 rounded-sm border-zinc-300" />
                  </div>
                )}

                {metodoEnvio === "foraneo" && (
                  <div data-testid="foraneo-form" className="mt-4 border-t border-zinc-100 pt-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-4">
                      <Globe className="w-4 h-4" /> En envíos foráneos el tiempo de entrega es hasta en 72 horas. El comercio te contactará para coordinar el costo de paquetería.
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Nombre completo" testId="for-nombre" value={foraneo.nombre} onChange={v => setF("nombre", v)} />
                      <Field label="Teléfono" testId="for-telefono" value={foraneo.telefono} onChange={v => setF("telefono", v)} />
                      <Field label="Calle y número" testId="for-calle" value={foraneo.calle} onChange={v => setF("calle", v)} className="sm:col-span-2" />
                      <Field label="Colonia" testId="for-colonia" value={foraneo.colonia} onChange={v => setF("colonia", v)} />
                      <Field label="Código postal" testId="for-cp" value={foraneo.codigo_postal} onChange={v => setF("codigo_postal", v)} maxLength={5} />
                      <Field label="Ciudad / Población" testId="for-ciudad" value={foraneo.ciudad} onChange={v => setF("ciudad", v)} />
                      <Field label="Estado" testId="for-estado" value={foraneo.estado} onChange={v => setF("estado", v)} />
                      <Field label="Referencias (opcional)" testId="for-ref" value={foraneo.referencias} onChange={v => setF("referencias", v)} className="sm:col-span-2" />
                    </div>
                  </div>
                )}
              </div>

              {/* Método de pago */}
              <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="overline text-zinc-500 mb-3">Método de pago</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {PAGOS.map(op => (
                    <button key={op.id} type="button" data-testid={`pago-${op.id}`} onClick={() => setMetodoPago(op.id)}
                      className={`text-left p-3 rounded-sm border-2 transition-colors flex gap-3 ${metodoPago === op.id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                      <op.Icon className="w-5 h-5 text-zinc-700 shrink-0" />
                      <div>
                        <div className="font-display font-bold text-sm">{op.titulo}</div>
                        <div className="text-[11px] text-zinc-500">{op.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-500 mt-3">Beneficiario del pago: <b>Marketing de Inteligencia Artificial S de RL de CV</b></p>
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm p-6 h-fit sticky top-24">
              <div className="overline text-zinc-500 mb-3">Resumen general</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> {metodoEnvio === "90min" ? "Envío 90 min" : metodoEnvio === "uber" ? "Envío por Uber" : "Envío foráneo"}</span>
                  <span data-testid="cart-envio">{metodoEnvio === "90min" ? `$${envio.toFixed(2)}` : "Por coordinar"}</span>
                </div>
                <div className="h-px bg-zinc-200 my-3" />
                <div className="flex justify-between font-display font-black text-2xl tracking-tight">
                  <span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span>
                </div>
              </div>
              <Button data-testid="cart-checkout" onClick={checkout} disabled={loading}
                className="w-full mt-5 h-12 rounded-sm bg-[#FF3366] hover:bg-[#E62958] text-white font-semibold">
                {loading ? "Procesando…" : metodoPago === "stripe" ? "Pagar con tarjeta" : "Confirmar y ver datos de transferencia"}
              </Button>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-zinc-500">
                <Shield className="w-3 h-3" /> Pago seguro procesado por la plataforma
              </div>
              {!user && <div className="mt-2 text-xs text-zinc-500 text-center">Debes iniciar sesión para comprar</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, className = "", ...rest }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</label>
      <Input data-testid={testId} value={value} onChange={e => onChange(e.target.value)} className="mt-1 rounded-sm border-zinc-300" {...rest} />
    </div>
  );
}
